export * from './interface'

interface PaymentLinkDetails {
    id: string
    payment_link_id: string
    status: string
    merchant_id: string
    merchant_reference_id: string
    short_link_unique_id: string
    amount: number
    currency: string
    mode: string
    encrypted_customer: string
    hashed_phone: string
    masked_phone: string
    hashed_email: string
    masked_email: string
    expire_at: string
    cancelled_at: string
    description: string
    created_at: string
    updated_at: string
    platform: string
    url: string
    customer: string
}

interface Refund {
    id: string
    merchant_id: number
    customer_id: number
    phone: string
    payment_id: string
    settlement_id: string
    amount: number
    refund_type: string
    bank_timestamp: string
    gokwik_rid: string
    transaction_npc_id: string
    source: string
    initiator: string
    refund_request_description: string
    refund_request_file: string
    status: string
    created_at: string
    updated_at: string
    order_number: string
    payment_provider: string
    is_auto_refund: boolean
    response_code: string
    settlement_commission_type: string
    bank_status: string
    status_description: string
    npc_id: string
    cust_ref_id: string
    failure_code: string
    timeout_status: boolean
    metadata: string
    transaction_cust_ref_id: string
    refunded_at: string
    m_id: string
    transaction_payment_id: string
}

interface Transaction {
    id: string
    merchant_id: string
    customer_id: number
    payment_id: string
    amount: number
    status: string
    is_active: boolean
    is_blocked: boolean
    is_deleted: boolean
    created_at: string
    updated_at: string
    order_number: string
    payment_provider: string
    is_auto_refund: boolean
    c2p_message_sent: boolean
    terminating_source: string
    initial_bank_status: string
    order_status: string
    upi_link: string
    bank_status: string
    npc_id: string
    cust_ref_id: string
    timeout_status: boolean
    metadata: string
    masked_phone: string
    payment_method: string
    encrypted_phone: string
    short_link: string
    encrypted_metadata: string
    is_retried: boolean
    source: string
    bucket1: string
    bucket2: string
}

interface PaymentLink {
    paymentLinkDetails: PaymentLinkDetails
    refunds: Refund[]
    transactions: Transaction[]
}

interface IPaymentLinkCustomerDetails {
    name?: string
    phone: string
    email?: string
}

interface IPaymentLinkNotifyCustomer {
    sms?: boolean
    whatsapp?: boolean
    email?: boolean
}

export interface CreatedPaymentLink {
    paymentLinkId: string
    status: string
    amount: number
    currency: string
    merchantReferenceId: string
    mode: string
    customer: IPaymentLinkCustomerDetails
    notify: IPaymentLinkNotifyCustomer | null
    description: string | null
    shortUrl: string
    expireAt: number
    cancelledAt: number | null
    webhookUrl: string | null
    redirectionUrl: {
        success: string | null
        failure: string | null
    }
}
interface ProviderField {
    label: string
    value: string
    key: string
    isRequired: boolean
}

interface TerminalsInputValues {
    subMerchantId: string
    salt?: string
    merchantKey?: string
    merchantEmail?: string
    clientId?: string
    clientSecret?: string
    merchantToken?: string
    tmsMerchantKey?: string
    tmsSalt?: string
    tmsSKey?: string
    tmsVector?: string
}

interface Instrument {
    instrument: string
    name: string
    enabled: boolean
    active?: boolean
    visible?: boolean
    isUpdated?: boolean
    instrument_config_presets?: { label: string; options: Record<string, unknown>[] }
    data?: {
        international_enabled?: boolean
        config?: Record<string, unknown>
    }
}

interface Method {
    method: string
    active?: boolean
    name: string
    enabled: boolean
    instruments: Instrument[]
    visible?: boolean
    isUpdated?: boolean
    isInstrumentsUpdated?: boolean
    isInternationalUpdated?: boolean
}

interface BluePrintInterface {
    methods: Method
    provider: string
    type: string
}

interface SelectDropdown {
    label: string
    value: string
}

interface SelectedProvider extends SelectDropdown {
    type?: string
}

interface MethodLevelConfig {
    method: string
    instrument?: string
    enabled: boolean
    merchantOverride: boolean
}
interface TerminalInterface {
    provider: string
    split: string | number
    type?: string
    name?: string
}

interface RouteInterface {
    id: number
    method: string
    instrument: string
    terminals: TerminalInterface[]
    merchant_id: string
    active?: boolean
    providers?: SelectDropdown[]
    name?: string
}

interface MethodsType {
    method: string
    name: string
}

enum ConfigType {
    TOGGLE = 'toggle',
    SELECT = 'select',
    MULTI_SELECT = 'multi_select',
    TEXT = 'text',
    NUMERIC = 'numeric',
    COLOR = 'color',
    DATE = 'date',
    LIST = 'list',
}

type ConfigValue = ConfigValueArray | string | number | boolean | Record<string, any> | Array<string>

type ConfigValueArray = (string | number)[]

interface Config {
    blueprint_id: string
    title: string
    description: string
    type: ConfigType
    value: ConfigValue
    validations: ConfigInputValidations
    category?: string
    platform?: string
    subcategory?: string
    name?: string
    default_value: ConfigValue
    metadata: Record<string, any>;
    oldValue?:ConfigValue
    options?: string[]
    categoryTitle?: string
    categoryDescription?: string
    subcategoryTitle?: string
    subcategoryDescription?: string
}

type ConfigInputValidationsOptions = ConfigValueArray

enum ConfigInputValidationsScheme {
    ALPHANUMERIC = 'alphanumeric',
    NUMERIC = 'numeric',
}

type ConfigInputValidationsText = {
    scheme: ConfigInputValidationsScheme
    min_length?: number
    max_length?: number
    min_number?: number
    max_number?: number
}

type ConfigInputValidationsSelect = {
    options_type: 'static' | 'dynamic'
    options?: ConfigInputValidationsOptions
}

type ConfigInputValidationsMultiSelect = {
    options_type: 'static' | 'dynamic'
    options?: ConfigInputValidationsOptions
}

type ConfigInputValidationsDate = {
    min_date: string
    max_date: string
}

type ConfigInputValidations =
    | ConfigInputValidationsText
    | ConfigInputValidationsMultiSelect
    | ConfigInputValidationsDate
    | Array<string>
    | Options

interface Options {
    options: string[]
}
interface Configs {
    [configName: string]: Config
}
interface ConfigSubcategory {
    description: string;
    title: string;
    configs: Configs
}
interface ConfigResponse {
    [key: string]: BaseConfig
}
interface ConfigCategory {
    [key: string]: Config[];
}

interface PaymentPageConfig {
    description: string;
    name: string;
    configs: ConfigCategory;
    title: string;
}

interface SubCategories {
    [key: string]: ConfigSubcategory
}
interface BaseConfig {
    description: string;
    subcategories: SubCategories
}

interface SimluatorConfigObject {
    paymentMethods: PaymentMethodsObject[],
    showPrice: boolean,
    showComparedPrice?: boolean
    pageTitle: string,
    pageTitleColor: string,
    paymentButtonsStyle: string,
    showDecimalValue: boolean,
    methodPositions?: Array<string>
}

interface PaymentMethodsObject {
    label?: string
    subtext?: string
    color?: string
    backgroundColor?: string
    name?: string
    badgeText?: string
    badgeColor?: string
    badgeBgColor?: string
    isEnabled?: boolean
    showPreferredPanel?: boolean,
    isPPCODEnabled?: boolean
}

interface ConfigDetailsInterface {
    config: ConfigCategory,
    name: string
}

export {
    PaymentLinkDetails,
    Refund,
    Transaction,
    PaymentLink,
    ProviderField,
    TerminalsInputValues,
    SelectedProvider,
    BluePrintInterface,
    Method,
    SelectDropdown,
    MethodLevelConfig,
    RouteInterface,
    TerminalInterface,
    MethodsType,
    ConfigResponse,
    ConfigSubcategory,
    Config,
    ConfigType,
    ConfigValue,
    ConfigValueArray,
    ConfigInputValidationsOptions,
    ConfigInputValidationsScheme,
    ConfigInputValidationsText,
    ConfigInputValidationsSelect,
    ConfigInputValidationsMultiSelect,
    ConfigInputValidationsDate,
    ConfigInputValidations,
    PaymentPageConfig,
    ConfigCategory,
    SubCategories,
    SimluatorConfigObject,
    PaymentMethodsObject,
    ConfigDetailsInterface,
    Options
}
